import { createSlice } from "@reduxjs/toolkit";
import {
  getAllCards,
  createCard,
  getCard,
  updateCard,
  deleteCard,
  toggleCardStatus,
  activateCard,
  toggleLowAmountCard,
  toggleHighAmountCard,
  setThresholdAmount,
  getActiveCards,
  getActiveCardForAmount,
} from "../thunks/Card";

const initialState = {
  cards: [],
  activeCards: [],
  selectedCardForAmount: null,
  selectedTrader: null,
  loading: false,
  error: null,
  isAddModalOpen: false,
  thresholdLoading: false,
};

const cardSlice = createSlice({
  name: "card",
  initialState,
  reducers: {
    openAddModal: (state) => {
      state.isAddModalOpen = true;
    },
    closeAddModal: (state) => {
      state.isAddModalOpen = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedTrader: (state, action) => {
      state.selectedTrader = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get All Cards
    builder
      .addCase(getAllCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCards.fulfilled, (state, action) => {
        state.loading = false;
        state.cards = action.payload.data.cards;
        state.error = null;
      })
      .addCase(getAllCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Card
    builder
      .addCase(createCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCard.fulfilled, (state, action) => {
        state.loading = false;
        state.cards.push(action.payload.data.card);
        state.isAddModalOpen = false;
        state.error = null;
      })
      .addCase(createCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Card
    builder
      .addCase(updateCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCard.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCard = action.payload.data.card;
        const cardIndex = state.cards.findIndex(
          (card) => card._id === updatedCard._id
        );
        if (cardIndex !== -1) {
          state.cards[cardIndex] = updatedCard;
        }
        state.isAddModalOpen = false;
        state.error = null;
      })
      .addCase(updateCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Card
    builder
      .addCase(deleteCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        state.loading = false;
        const cardId = action.payload;
        state.cards = state.cards.filter((card) => card._id !== cardId);
        state.error = null;
      })
      .addCase(deleteCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Toggle Card Status
    builder
      .addCase(toggleCardStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleCardStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCard = action.payload.data.card;
        const cardIndex = state.cards.findIndex(
          (card) => card._id === updatedCard._id
        );
        if (cardIndex !== -1) {
          state.cards[cardIndex] = updatedCard;
        }
        state.error = null;
      })
      .addCase(toggleCardStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Activate Card
    builder
      .addCase(activateCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateCard.fulfilled, (state, action) => {
        state.loading = false;
        // Backend already handles deactivating all other cards
        // So we need to refresh all cards or update them accordingly
        const activatedCard = action.payload.data.card;
        state.cards.forEach((card) => {
          card.isActive = card._id === activatedCard._id;
        });
        state.error = null;
      })
      .addCase(activateCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Toggle Low Amount Card
    builder
      .addCase(toggleLowAmountCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleLowAmountCard.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCard = action.payload.data.card;
        const cardIndex = state.cards.findIndex(
          (card) => card._id === updatedCard._id
        );
        if (cardIndex !== -1) {
          state.cards[cardIndex] = updatedCard;
        }
        state.error = null;
      })
      .addCase(toggleLowAmountCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Toggle High Amount Card
    builder
      .addCase(toggleHighAmountCard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleHighAmountCard.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCard = action.payload.data.card;
        const cardIndex = state.cards.findIndex(
          (card) => card._id === updatedCard._id
        );
        if (cardIndex !== -1) {
          state.cards[cardIndex] = updatedCard;
        }
        state.error = null;
      })
      .addCase(toggleHighAmountCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Set Threshold Amount
    builder
      .addCase(setThresholdAmount.pending, (state) => {
        state.thresholdLoading = true;
        state.error = null;
      })
      .addCase(setThresholdAmount.fulfilled, (state, action) => {
        state.thresholdLoading = false;
        const updatedCard = action.payload.data.card;
        const cardIndex = state.cards.findIndex(
          (card) => card._id === updatedCard._id
        );
        if (cardIndex !== -1) {
          state.cards[cardIndex] = updatedCard;
        }
        state.error = null;
      })
      .addCase(setThresholdAmount.rejected, (state, action) => {
        state.thresholdLoading = false;
        state.error = action.payload;
      });

    // Get Active Cards
    builder
      .addCase(getActiveCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveCards.fulfilled, (state, action) => {
        state.loading = false;
        state.activeCards = action.payload.data.cards;
        state.error = null;
      })
      .addCase(getActiveCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get Active Card For Amount
    builder
      .addCase(getActiveCardForAmount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveCardForAmount.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCardForAmount = action.payload.data.activeCard;
        state.error = null;
      })
      .addCase(getActiveCardForAmount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { openAddModal, closeAddModal, clearError, setSelectedTrader } =
  cardSlice.actions;

export default cardSlice.reducer;
